import { Loader2, CheckCircle2, XCircle } from "lucide-react";

/**
 * QueueProgressPill — compact status pill for the generation queue.
 *
 * Two variants:
 *   • "floating" (default) — legacy behaviour, fixed bottom-center just above
 *     the ChatPromptDock. Kept for pages that still want a heads-up chip
 *     floating outside the dock.
 *   • "inline" — no fixed wrapper. Renders just the pill button so it can be
 *     dropped into a toolbar row (e.g. inside the ChatPromptDock next to
 *     the GENERATE button). Right-aligned by the parent flex container.
 *
 * Auto-hides at 0 items in both variants. Click scrolls to the first active
 * feed item (parent-supplied via `onClick`).
 */
export function QueueProgressPill({
  items = [],
  onClick,
  className = "",
  variant = "floating",
  testId = "queue-progress-pill",
}) {
  const active = items.filter((i) => i.status === "queued" || i.status === "generating").length;
  const done   = items.filter((i) => i.status === "done").length;
  const failed = items.filter((i) => i.status === "error").length;

  if (!items.length) return null;

  const hasActive = active > 0;

  const button = (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`pointer-events-auto inline-flex items-center gap-2.5 px-4 py-2 rounded-full border backdrop-blur-2xl transition-all font-mono-hy text-[10px] tracking-[0.2em] whitespace-nowrap ${
        hasActive
          ? "border-[#F2C94C]/45 bg-[#F2C94C]/10 text-[#F2C94C] shadow-[0_0_36px_-8px_rgba(242,201,76,0.65)]"
          : failed > 0
          ? "border-[#EB5757]/45 bg-[#EB5757]/10 text-[#EB5757] shadow-[0_0_30px_-8px_rgba(235,87,87,0.55)]"
          : "border-[#00E5FF]/45 bg-[#00E5FF]/10 text-[#00E5FF] shadow-[0_0_30px_-8px_rgba(0,229,255,0.55)]"
      } ${className}`}
    >
      {hasActive ? (
        <>
          <span className="relative flex items-center justify-center w-4 h-4">
            <span className="absolute inset-0 rounded-full bg-[#F2C94C]/40 animate-ping" />
            <Loader2 className="w-3.5 h-3.5 animate-spin relative" />
          </span>
          <span>{active} COOKING</span>
          {done > 0 && <span className="opacity-70">· {done} DONE</span>}
          {failed > 0 && <span className="opacity-70">· {failed} FAILED</span>}
        </>
      ) : failed > 0 ? (
        <>
          <XCircle className="w-3.5 h-3.5" />
          <span>{done} DONE</span>
          <span>· {failed} FAILED</span>
        </>
      ) : (
        <>
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span>ALL {done} DONE</span>
        </>
      )}
    </button>
  );

  if (variant === "inline") return button;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-[240px] z-50 pointer-events-none"
      data-testid={`${testId}-wrap`}
    >
      {button}
    </div>
  );
}

export default QueueProgressPill;
